import type { AvatarConfig, CityAsset, HouseData } from "../api/types";
import { CityApi } from "../api/client";
import { AVATAR_APPEARANCES,AVATAR_BODY_HEIGHT,resolveAvatarAppearance } from '../player/avatarAppearance';

import {CharacterCustomizer} from './CharacterCustomizer';
import {CHARACTER_CATALOG, type CharacterAppearance} from '../player/characterAppearance';
import {characterImage} from '../player/characterImages';
import {portraitBounds,portraitScale} from '../player/portraitFrame';

type PreviewTransform = {
  width: number;
  height: number;
  sceneX: number;
  sceneY: number;
  scale: number;
  parcelWidth: number;
  parcelDepth: number;
};

const SVG_NS = "http://www.w3.org/2000/svg";

function cloneAvatar(value?: AvatarConfig): AvatarConfig {
  if (value?.points?.length) return JSON.parse(JSON.stringify(value)) as AvatarConfig;
  return {
    enabled: false,
    assetId: "resident-blue",
    speed: 1,
    points: [{ x: 50, y: 50, wait: 0 }],
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export class StoreAvatarEditor {
  private readonly root = document.createElement("div");
  private readonly button = document.createElement("button");
  private readonly panel = document.createElement("section");
  private readonly overlay = document.createElementNS(SVG_NS, "svg");
  private readonly enabled = document.createElement("input");
  private readonly chooser = document.createElement("select");
  private readonly appearanceChoices = document.createElement('div');
  private readonly speed = document.createElement("input");
  private readonly wait = document.createElement("input");
  private readonly removePoint = document.createElement("button");
  private readonly status = document.createElement("small");
  private house: HouseData | null = null;
  private config: AvatarConfig = cloneAvatar();
  private transform: PreviewTransform | null = null;
  private selectedPoint = 0;
  private editing = false;
  private routeMode = false;
  private readonly customizer:CharacterCustomizer;
  private customPortrait?:string;
  private dirty = false;
  private changeVersion = 0;
  private portraitHost: HTMLElement | null = null;
  private portraitCanvas: HTMLCanvasElement | null = null;
  private portraitPlaceholder: HTMLImageElement | null = null;
  private portraitStatus: HTMLElement | null = null;
  private portraitFrame?:HTMLCanvasElement;
  private portraitResize?:ResizeObserver;
  private portraitView = 0;
  private portraitVersion = 0;
  private readonly frames = new Map<string, Promise<HTMLCanvasElement>>();
  private readonly atlases = new Map<string,Promise<HTMLImageElement>>();

  constructor(
    private readonly api: CityApi,
    private readonly assets: CityAsset[],
    private readonly host: HTMLElement,
    private readonly onChanged: (house: HouseData) => void,
    private readonly sidebar?: HTMLElement,
    private readonly onRouteMode:(route:boolean)=>void=()=>{},
  ) {
    this.customizer=new CharacterCustomizer(value=>this.chooseCustom(value),route=>{
      this.routeMode=route;this.overlay.classList.toggle('is-editing',this.editing&&route);this.onRouteMode(route);this.renderRoute();
    });
    this.root.className = "city-store-avatar-controls";
    this.button.type = "button";
    this.button.className = "city-store-avatar-button";
    this.button.textContent = "+ افزودن آواتار";
    this.button.addEventListener("click", () => this.toggleEditor());

    this.panel.className = "city-store-avatar-panel";
    this.panel.hidden = true;

    const titleRow = document.createElement("div");
    titleRow.className = "city-store-avatar-panel__title";
    const title = document.createElement("strong");
    title.textContent = "آواتار و مسیر";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "city-store-avatar-panel__close";
    close.textContent = "×";
    close.setAttribute("aria-label", "بستن");
    close.addEventListener("click", () => this.setEditing(false));
    titleRow.append(title, close);

    this.enabled.type = "checkbox";
    const enabledLabel = document.createElement("label");
    enabledLabel.className = "city-store-avatar-check";
    enabledLabel.append(this.enabled, document.createTextNode(" نمایش آواتار"));
    this.enabled.addEventListener("change", () => {
      this.config.enabled = this.enabled.checked;
      this.markDirty();
      this.updateButton();
      this.renderRoute();
    });

    const chooserLabel = document.createElement("label");
    chooserLabel.textContent = "انتخاب آواتار";
    const options = new Map<string, string>(AVATAR_APPEARANCES.map(a=>[a.assetId,a.name??a.assetId]));
    for (const asset of this.assets) {
      const isAvatar = ["avatar", "avatars", "character", "characters"].includes(asset.category ?? "")
        || asset.tags?.includes("avatar");
      if (isAvatar&&!resolveAvatarAppearance(asset.assetId)) options.set(asset.assetId, asset.name ?? asset.assetId);
    }
    for (const [value, label] of options) {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      this.chooser.appendChild(option);
    }
    this.chooser.addEventListener("change", () => {
      this.customizer.usePreset();
      delete this.config.appearance;
      this.customPortrait=undefined;
      this.config.assetId = this.chooser.value;
      this.updateButton();
      this.markDirty();
      this.syncAppearanceChoices();
      this.refreshPortrait();
      this.renderRoute();
    });
    this.chooser.hidden=true;
    chooserLabel.appendChild(this.chooser);
    this.appearanceChoices.className='city-avatar-appearances';
    this.appearanceChoices.setAttribute('role','group');
    this.appearanceChoices.setAttribute('aria-label','انتخاب ظاهر آواتار');
    for(const [id,name]of options){
      const asset=resolveAvatarAppearance(id)??this.assets.find(a=>a.assetId===id);
      const button=document.createElement('button');button.type='button';button.className='city-avatar-appearance';
      button.dataset.avatarId=id;button.setAttribute('aria-label',name);button.setAttribute('aria-pressed','false');
      if(asset?.thumbnailUrl||asset?.sourceUrl){const image=document.createElement('img');image.src=asset.thumbnailUrl??asset.sourceUrl!;image.alt='';image.width=80;image.height=96;image.decoding='async';button.append(image);}
      const caption=document.createElement('span');caption.textContent=name;button.append(caption);
      button.addEventListener('click',()=>{this.customizer.usePreset();delete this.config.appearance;this.customPortrait=undefined;this.chooser.value=id;this.config.assetId=id;this.updateButton();this.markDirty();this.syncAppearanceChoices();this.refreshPortrait();this.renderRoute();});
      this.appearanceChoices.append(button);
    }
    chooserLabel.append(this.appearanceChoices);

    const speedLabel = document.createElement("label");
    speedLabel.textContent = "سرعت حرکت";
    this.speed.type = "number";
    this.speed.min = "0.1";
    this.speed.max = "3";
    this.speed.step = "0.1";
    this.speed.addEventListener("change", () => {
      this.config.speed = clamp(Number(this.speed.value) || 1, 0.1, 3);
      this.speed.value = String(this.config.speed);
      this.markDirty();
    });
    speedLabel.appendChild(this.speed);

    const hint = document.createElement("p");
    hint.className = "city-store-avatar-hint";
    hint.textContent = "روی همین زمین نقطه بگذار؛ هر نقطه را بکش و جابه‌جا کن. مسیر از محدوده زمین خارج نمی‌شود.";

    const pointRow = document.createElement("div");
    pointRow.className = "city-store-avatar-point-controls";
    const waitLabel = document.createElement("label");
    waitLabel.textContent = "مکث (ثانیه)";
    this.wait.type = "number";
    this.wait.min = "0";
    this.wait.max = "30";
    this.wait.step = "0.1";
    this.wait.addEventListener("change", () => {
      const point = this.config.points[this.selectedPoint];
      if (!point) return;
      point.wait = clamp(Number(this.wait.value) || 0, 0, 30);
      this.wait.value = String(point.wait);
      this.markDirty();
    });
    waitLabel.appendChild(this.wait);
    this.removePoint.type = "button";
    this.removePoint.className = "city-store-avatar-remove";
    this.removePoint.textContent = "حذف نقطه";
    this.removePoint.addEventListener("click", () => {
      if (this.config.points.length <= 1) return;
      this.config.points.splice(this.selectedPoint, 1);
      this.selectedPoint = Math.max(0, Math.min(this.selectedPoint, this.config.points.length - 1));
      this.markDirty();
      this.syncPointControls();
      this.renderRoute();
    });
    pointRow.append(waitLabel, this.removePoint);

    const actions = document.createElement("div");
    actions.className = "city-store-avatar-actions";
    const save = document.createElement("button");
    save.type = "button";
    save.className = "city-btn city-btn--primary";
    save.textContent = "ذخیره آواتار";
    save.addEventListener("click", () => {
      void this.persist(!this.sidebar).catch(() => undefined);
    });
    if(!this.sidebar)actions.append(save);

    this.status.className = "city-store-avatar-status";
    this.status.hidden = true;
    const routeOnly = document.createElement("button");
    routeOnly.type = "button";
    routeOnly.className = "city-btn city-store-avatar-route-edit";
    routeOnly.textContent = "کشیدن نقاط مسیر روی زمین";
    routeOnly.hidden=!!this.sidebar;
    routeOnly.addEventListener("click", () => { if(!this.sidebar)this.panel.hidden = true; });
    const routes=document.createElement('section');routes.className='city-workshop-route';
    routes.append(speedLabel,hint,pointRow);
    this.customizer.setPanels(this.appearanceChoices,routes);
    this.panel.append(enabledLabel,this.customizer.element,this.status,actions);
    this.root.append(this.button, this.panel);

    this.overlay.classList.add("city-store-avatar-overlay");
    this.overlay.setAttribute("aria-label", "مسیر حرکت آواتار روی زمین مغازه");
    this.overlay.addEventListener("pointerdown", (event) => {
      if (!this.editing || !this.routeMode || event.target !== this.overlay && !(event.target instanceof SVGPolygonElement)) return;
      const point = this.screenToPercent(event.clientX, event.clientY, false);
      if (!point || this.config.points.length >= 30) return;
      this.config.points.push({ x: point.x, y: point.y, wait: 0 });
      this.selectedPoint = this.config.points.length - 1;
      this.markDirty();
      this.syncPointControls();
      this.renderRoute();
    });
  }

  private chooseCustom(value:CharacterAppearance):void{
    this.config.assetId=CHARACTER_CATALOG.assetId;this.config.appearance=value;
    this.markDirty();this.syncAppearanceChoices();this.refreshPortrait();this.renderRoute();
  }

  /** The workshop owns visibility; route gestures only run in the route category. */
  showEditor(visible:boolean):void {
    this.setEditing(visible);
    if(visible)this.refreshPortrait();
  }

  attachPortrait(host:HTMLElement):void {
    this.portraitHost=host;
    host.innerHTML=[
      '<div class="city-studio-v2__portrait-title"><span>شخصیت من</span><small>ظاهر انتخاب‌شده در شهر نمایش داده می‌شود</small></div>',
      '<div class="city-studio-v2__portrait-art"><img class="city-studio-v2__portrait-placeholder" alt="" /><canvas width="160" height="192" role="img" aria-label="پیش‌نمایش نزدیک شخصیت"></canvas><span role="status">آماده‌سازی شخصیت…</span></div>',
      '<div class="city-studio-v2__portrait-views"><button type="button" data-avatar-view="0" aria-pressed="true">روبه‌رو</button><button type="button" data-avatar-view="1" aria-pressed="false">سه‌رخ</button></div>'
    ].join('');
    this.portraitCanvas=host.querySelector('canvas');
    this.portraitResize?.disconnect();
    this.portraitResize=new ResizeObserver(()=>this.drawPortrait());
    this.portraitResize.observe(host.querySelector('.city-studio-v2__portrait-art')!);
    this.portraitPlaceholder=host.querySelector<HTMLImageElement>('.city-studio-v2__portrait-placeholder');
    this.portraitStatus=host.querySelector<HTMLElement>('[role="status"]');
    for(const button of host.querySelectorAll<HTMLButtonElement>('[data-avatar-view]')){
      button.onclick=()=>{
        this.portraitView=Number(button.dataset.avatarView);
        for(const other of host.querySelectorAll<HTMLButtonElement>('[data-avatar-view]'))other.setAttribute('aria-pressed',String(other===button));
        this.refreshPortrait();
      };
    }
  }

  /** Share each downloaded atlas between its eight viewing angles. */
  private atlasFor(url:string):Promise<HTMLImageElement>{
    const cached=this.atlases.get(url);if(cached)return cached;
    const pending=new Promise<HTMLImageElement>((resolve,reject)=>{
      const image=new Image();image.decoding='async';
      image.onload=()=>resolve(image);
      image.onerror=()=>reject(new Error('تصویر آواتار بارگذاری نشد.'));
      image.src=url;
    });
    this.atlases.set(url,pending);
    void pending.catch(()=>this.atlases.delete(url));
    return pending;
  }

  /** Draw one still frame. No additional network round trip or server rendering. */
  private frameFor(id:string,row:number):Promise<HTMLCanvasElement>{
    const key=id+':'+row;
    const cached=this.frames.get(key);if(cached)return cached;
    const asset=resolveAvatarAppearance(id)??this.assets.find(item=>item.assetId===id);
    if(!asset?.sourceUrl)return Promise.reject(new Error('تصویر این آواتار موجود نیست.'));
    const task=this.atlasFor(asset.sourceUrl).then(image=>{
      const frameWidth=asset.spriteSheet?.frameWidth??image.naturalWidth;
      const frameHeight=asset.spriteSheet?.frameHeight??image.naturalHeight;
      const rows=asset.spriteSheet?.rows??1;
      const canvas=document.createElement('canvas');
      canvas.width=frameWidth;canvas.height=frameHeight;
      const context=canvas.getContext('2d');
      if(!context)throw new Error('Canvas unavailable');
      context.clearRect(0,0,frameWidth,frameHeight);
      context.drawImage(image,0,Math.min(row,rows-1)*frameHeight,frameWidth,frameHeight,0,0,frameWidth,frameHeight);
      return canvas;
    });
    this.frames.set(key,task);
    void task.catch(()=>this.frames.delete(key));
    return task;
  }

  private refreshPortrait():void {
    const id=this.config.assetId;
    const appearance=this.config.appearance;
    const version=++this.portraitVersion;
    const canvas=this.portraitCanvas;
    if(!canvas)return;
    const asset=resolveAvatarAppearance(id)??this.assets.find(item=>item.assetId===id);
    const placeholder=this.portraitPlaceholder;
    if(placeholder){
      const thumbnail=asset?.thumbnailUrl??asset?.sourceUrl;
      if(thumbnail){placeholder.src=thumbnail;placeholder.hidden=false;}
      else{placeholder.removeAttribute('src');placeholder.hidden=true;}
    }
    canvas.style.opacity='0';
    const status=this.portraitStatus;
    if(status){status.hidden=false;status.textContent='در حال نمایش شخصیت…';}
    const framePromise=appearance?characterImage(appearance,true,this.portraitView).then(bitmap=>{
      try{const frame=document.createElement('canvas');frame.width=80;frame.height=96;frame.getContext('2d')!.drawImage(bitmap,0,0);return frame;}finally{bitmap.close();}
    }):this.frameFor(id,this.portraitView);
    void framePromise.then(frame=>{
      if(version!==this.portraitVersion||!this.portraitCanvas)return;
      this.portraitFrame=frame;this.drawPortrait();
      this.portraitCanvas.style.opacity='1';
      if(appearance){this.customPortrait=frame.toDataURL('image/png');this.renderRoute();}
      if(placeholder)placeholder.hidden=true;
      if(status)status.hidden=true;
    }).catch(()=>{
      if(version!==this.portraitVersion)return;
      if(status){status.hidden=false;status.textContent='پیش‌نمایش نزدیک در دسترس نیست.';}
    });

  }

  private drawPortrait():void {
    const frame=this.portraitFrame,target=this.portraitCanvas,host=target?.parentElement;
    if(!frame||!target||!host||!host.clientHeight)return;
    const pixels=frame.getContext('2d')!.getImageData(0,0,frame.width,frame.height).data;
    const crop=portraitBounds(pixels,frame.width,frame.height);
    const scale=portraitScale(crop.width,crop.height,host.clientWidth-32,host.clientHeight-28);
    const density=Math.max(1,Math.round(window.devicePixelRatio||1));
    target.width=Math.round(crop.width*scale*density);target.height=Math.round(crop.height*scale*density);
    target.style.width=`${crop.width*scale}px`;target.style.height=`${crop.height*scale}px`;
    const context=target.getContext('2d')!;context.imageSmoothingEnabled=false;
    context.drawImage(frame,crop.x,crop.y,crop.width,crop.height,0,0,target.width,target.height);
  }

  mount(): void {
    this.host.classList.add("city-store-avatar-host");
    if (!this.overlay.isConnected) this.host.appendChild(this.overlay);
    if (!this.root.isConnected) this.host.appendChild(this.root);
    if(this.sidebar){this.panel.classList.add('city-store-avatar-panel--sidebar');if(this.panel.parentElement!==this.sidebar)this.sidebar.prepend(this.panel);}
  }

  open(house: HouseData): void {
    this.changeVersion++;
    this.house = house;
    this.config = cloneAvatar(house.avatar);
    this.selectedPoint = 0;
    this.dirty = false;
    this.enabled.checked = this.config.enabled;
    this.chooser.value = resolveAvatarAppearance(this.config.assetId)?.assetId??this.config.assetId;
    if (!this.chooser.value) this.chooser.value = AVATAR_APPEARANCES[0].assetId;
    if(!this.config.appearance)this.config.assetId = this.chooser.value;
    this.customPortrait=undefined;this.customizer.setValue(this.config.appearance);
    this.syncAppearanceChoices();
    this.speed.value = String(this.config.speed || 1);
    this.status.hidden = true;
    this.setEditing(false);
    this.syncPointControls();
    this.updateButton();
    // The studio opens in the shop tab. Defer costly character composites,
    // frame decoding and portrait canvases until the avatar tab is selected.
    this.portraitVersion++;
    this.portraitFrame=undefined;
  }

  close(): void {
    this.changeVersion++;
    this.setEditing(false);
  }

  destroy(): void {
    this.customizer.destroy();
    this.portraitVersion++;
    this.portraitResize?.disconnect();this.portraitFrame=undefined;
    this.root.remove();
    this.panel.remove();
    this.overlay.remove();
    this.sidebar?.classList.remove('is-avatar-editing');
    this.frames.clear();
    this.atlases.clear();
    this.portraitHost=null;
    this.portraitCanvas=null;
    this.portraitPlaceholder=null;
    this.portraitStatus=null;
  }

  updatePreview(transform: PreviewTransform): void {
    this.transform = transform;
    this.overlay.setAttribute("viewBox", `0 0 ${transform.width} ${transform.height}`);
    this.renderRoute();
  }

  getCurrent():AvatarConfig{return cloneAvatar(this.config);}

  getDraft(): AvatarConfig | undefined {
    return this.dirty ? cloneAvatar(this.config) : undefined;
  }

  acceptSaved(house: HouseData): void {
    const changed = this.dirty;
    this.house = house;
    this.config = cloneAvatar(house.avatar ?? this.config);
    this.dirty = false;
    if (changed) this.onChanged(house);
  }

  private toggleEditor(): void {
    if (!this.house) return;
    if (!this.config.enabled) {
      this.config.enabled = true;
      this.enabled.checked = true;
      this.markDirty();
    }
    if (this.editing && this.panel.hidden) { this.panel.hidden = false; return; }
    this.setEditing(!this.editing);
  }

  private setEditing(editing: boolean): void {
    if (!editing) this.activeDrag = null;
    this.editing = editing;
    this.panel.hidden = !editing;
    this.sidebar?.classList.toggle('is-avatar-editing',editing);
    this.overlay.classList.toggle("is-editing", editing&&this.routeMode);
    this.renderRoute();
  }

  private updateButton(): void {
    this.button.textContent = this.config.enabled ? "آواتار و مسیر" : "+ افزودن آواتار";
  }

  private markDirty(): void {
    this.changeVersion++;
    this.dirty = true;
    this.status.hidden = true;
  }

  private syncAppearanceChoices():void {
    for(const button of this.appearanceChoices.querySelectorAll<HTMLButtonElement>('button')){
      button.setAttribute('aria-pressed',String(button.dataset.avatarId===this.config.assetId));
    }
  }

  private safeMargins(): { x: number; y: number } {
    const width = Math.max(1, this.transform?.parcelWidth ?? this.house?.placement.parcelBounds?.width ?? 5.5);
    const depth = Math.max(1, this.transform?.parcelDepth ?? this.house?.placement.parcelBounds?.depth ?? 5.5);
    return { x: Math.min(30, 25 / width), y: Math.min(30, 25 / depth) };
  }

  private percentToScreen(x: number, y: number): { x: number; y: number } | null {
    const t = this.transform;
    if (!t) return null;
    const wx = (x / 100 - 0.5) * t.parcelWidth;
    const wy = (y / 100 - 0.5) * t.parcelDepth;
    return {
      x: t.sceneX + (wx - wy) * 32 * t.scale,
      y: t.sceneY + (wx + wy) * 16 * t.scale,
    };
  }

  private screenToPercent(clientX: number, clientY: number, allowOutside = true): { x: number; y: number } | null {
    const t = this.transform;
    if (!t) return null;
    const box = this.overlay.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    const sx = (clientX - box.left) * (t.width / box.width) - t.sceneX;
    const sy = (clientY - box.top) * (t.height / box.height) - t.sceneY;
    const wx = sx / (64 * t.scale) + sy / (32 * t.scale);
    const wy = sy / (32 * t.scale) - sx / (64 * t.scale);
    const rawX = (wx / t.parcelWidth + 0.5) * 100;
    const rawY = (wy / t.parcelDepth + 0.5) * 100;
    if (!allowOutside && (rawX < 0 || rawX > 100 || rawY < 0 || rawY > 100)) return null;
    const margin = this.safeMargins();
    return {
      x: Number(clamp(rawX, margin.x, 100 - margin.x).toFixed(2)),
      y: Number(clamp(rawY, margin.y, 100 - margin.y).toFixed(2)),
    };
  }

  private syncPointControls(): void {
    const point = this.config.points[this.selectedPoint];
    this.wait.disabled = !point;
    this.wait.value = String(point?.wait ?? 0);
    this.removePoint.disabled = this.config.points.length <= 1;
  }

  private activeDrag: number | null = null;

  private renderRoute(): void {
    if (this.activeDrag !== null) return;
    this.overlay.replaceChildren();
    if (!this.editing || !this.routeMode || !this.transform || !this.config.enabled) return;

    const margin = this.safeMargins();
    const safePoints = [
      [margin.x, margin.y],
      [100 - margin.x, margin.y],
      [100 - margin.x, 100 - margin.y],
      [margin.x, 100 - margin.y],
    ].map(([x, y]) => this.percentToScreen(x, y)).filter(Boolean) as Array<{x:number;y:number}>;
    const safe = document.createElementNS(SVG_NS, "polygon");
    safe.classList.add("city-store-avatar-safe");
    safe.setAttribute("points", safePoints.map((point) => `${point.x},${point.y}`).join(" "));
    this.overlay.appendChild(safe);

    const routePoints = this.config.points.map((point) => this.percentToScreen(point.x, point.y)).filter(Boolean) as Array<{x:number;y:number}>;
    if (routePoints.length) {
      const line = document.createElementNS(SVG_NS, "polyline");
      line.classList.add("city-store-avatar-route");
      const closed = [...routePoints, routePoints[0]];
      line.setAttribute("points", closed.map((point) => `${point.x},${point.y}`).join(" "));
      this.overlay.appendChild(line);
    }

    this.config.points.forEach((point, index) => {
      const screen = this.percentToScreen(point.x, point.y);
      if (!screen) return;
      const circle = document.createElementNS(SVG_NS, "circle");
      circle.classList.add("city-store-avatar-point");
      if (index === this.selectedPoint) circle.classList.add("is-selected");
      circle.setAttribute("cx", String(screen.x));
      circle.setAttribute("cy", String(screen.y));
      circle.setAttribute("r", String(12 * this.transform!.width / Math.max(1, this.overlay.getBoundingClientRect().width)));
      circle.setAttribute("aria-label", `نقطه ${index + 1}`);
      circle.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.selectedPoint = index;
        this.activeDrag = event.pointerId;
        circle.setPointerCapture(event.pointerId);
        circle.classList.add("is-selected");
        this.syncPointControls();
      });
      circle.addEventListener("pointermove", (event) => {
        if (!circle.hasPointerCapture(event.pointerId)) return;
        const next = this.screenToPercent(event.clientX, event.clientY);
        if (!next) return;
        point.x = next.x;
        point.y = next.y;
        this.markDirty();
        const screen = this.percentToScreen(point.x, point.y);
        if (screen) {
          circle.setAttribute("cx", String(screen.x));
          circle.setAttribute("cy", String(screen.y));
          const marker = this.overlay.querySelector(".city-store-avatar-marker");
          marker?.setAttribute("transform", `translate(${screen.x} ${screen.y})`);
          const image = this.overlay.querySelector(".city-store-avatar-marker-image");
          if(image){
            image.setAttribute('x',String(screen.x-Number(image.getAttribute('width'))/2));
            image.setAttribute('y',String(screen.y-Number(image.getAttribute('height'))*Number(image.getAttribute('data-anchor-y')??1)));
          }
        }
        const points = this.config.points.map(p => this.percentToScreen(p.x, p.y)).filter(Boolean) as Array<{x:number;y:number}>;
        if (points.length) this.overlay.querySelector(".city-store-avatar-route")?.setAttribute("points", [...points, points[0]].map(p => `${p.x},${p.y}`).join(" "));
      });
      const release = (event: PointerEvent) => {
        if (this.activeDrag !== event.pointerId) return;
        this.activeDrag = null;
        if (circle.hasPointerCapture(event.pointerId)) circle.releasePointerCapture(event.pointerId);
        this.syncPointControls();
        this.renderRoute();
      };
      circle.addEventListener("pointerup", release);
      circle.addEventListener("pointercancel", release);
      circle.addEventListener("lostpointercapture", release);
      this.overlay.appendChild(circle);
    });

    const selected = this.config.points[this.selectedPoint];
    const marker = selected ? this.percentToScreen(selected.x, selected.y) : null;
    if (marker) this.renderAvatarMarker(marker.x, marker.y);
  }

  private renderAvatarMarker(x: number, y: number): void {
    const asset = resolveAvatarAppearance(this.config.assetId)??this.assets.find((item) => item.assetId === this.config.assetId);
    if (asset?.sourceUrl||this.customPortrait) {
      const image = document.createElementNS(SVG_NS, "image");
      image.classList.add("city-store-avatar-marker-image");
      const zoom=this.transform?.scale??1;
      const height=this.customPortrait?AVATAR_BODY_HEIGHT*96/72*zoom:asset!.avatarAnimation?AVATAR_BODY_HEIGHT*asset!.spriteSheet!.frameHeight/asset!.avatarAnimation!.bodyHeight*zoom:AVATAR_BODY_HEIGHT*zoom;
      const width=this.customPortrait?height*80/96:asset!.avatarAnimation?height*asset!.spriteSheet!.frameWidth/asset!.spriteSheet!.frameHeight:height;
      const anchor=this.customPortrait?85/96:asset!.avatarAnimation?(asset!.anchorY??1):1;
      image.setAttribute("href", this.customPortrait??asset!.thumbnailUrl??asset!.sourceUrl!);
      image.setAttribute("x", String(x-width/2));
      image.setAttribute("y", String(y-height*anchor));
      image.setAttribute("width", String(width));
      image.setAttribute("height", String(height));
      image.setAttribute('data-anchor-y',String(anchor));
      image.setAttribute("preserveAspectRatio", "xMidYMax meet");
      this.overlay.appendChild(image);
      return;
    }
    const group = document.createElementNS(SVG_NS, "g");
    group.classList.add("city-store-avatar-marker");
    group.setAttribute("transform", `translate(${x} ${y})`);
    const body = document.createElementNS(SVG_NS, "rect");
    body.setAttribute("x", "-7"); body.setAttribute("y", "-23"); body.setAttribute("width", "14"); body.setAttribute("height", "17"); body.setAttribute("rx", "5");
    const head = document.createElementNS(SVG_NS, "circle");
    head.setAttribute("cx", "0"); head.setAttribute("cy", "-29"); head.setAttribute("r", "6");
    group.append(body, head);
    this.overlay.appendChild(group);
  }

  private async persist(closeOnSuccess: boolean): Promise<void> {
    if (!this.house) return;
    const target = this.house;
    const version = this.changeVersion;
    this.status.hidden = false;
    this.status.textContent = "در حال ذخیره…";
    try {
      const result = await this.api.saveResident(target.externalId, { avatar: cloneAvatar(this.config) });
      this.onChanged(result.storefront);
      if (version !== this.changeVersion) return;
      Object.assign(target, result.storefront);
      this.config = cloneAvatar(result.storefront.avatar ?? this.config);
      this.dirty = false;
      this.status.textContent = "آواتار و مسیر ذخیره شد.";
      this.updateButton();
      if (closeOnSuccess) this.setEditing(false);
    } catch (error) {
      if (version === this.changeVersion) this.status.textContent = error instanceof Error ? error.message : "ذخیره انجام نشد.";
      throw error;
    }
  }
}
